import { skipToken } from "@reduxjs/toolkit/query";
import {
  AutomationTemplateIds,
  type AutomationId,
  type AutomationTemplateId,
} from "@repo/app-types/entities/automation";
import type { AutomationConfiguration } from "@repo/app-types/entities/automationProposal";
import {
  copilotProposalIdSchema,
  type CopilotProposalId,
} from "@repo/app-types/entities/copilotProposal";
import {
  asPlaybookId,
  type PlaybookId,
} from "@repo/app-types/entities/playbook";
import { FieldError } from "@repo/ui/field/fieldError";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { useState, type FC } from "react";
import {
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router";
import { v4 as uuidv4 } from "uuid";
import {
  useCreateAutomationMutation,
  useLazyAutomationByIdQuery,
} from "../../api/entities/automation.js";
import {
  useGetPendingAutomationProposalQuery,
  useResolveProposalMutation,
} from "../../api/entities/copilotProposal.js";
import { isConflictQueryError } from "../../api/queryErrors.js";
import { appPaths } from "../../app/appPaths.js";
import { requireRouteParam } from "../../app/routeParams.js";
import {
  PageBody,
  PageLayout,
  PageLayoutHeight,
} from "../../components/layout/pageLayout.js";
import { CopilotProposalBanner } from "../../components/recordingAssessmentEditor/copilotProposalBanner.js";
import { AutomationConfigForm } from "../../features/automations/automationConfigForm.js";
import { AUTOMATION_PROPOSAL_QUERY_PARAM } from "../../features/automations/automationEditorUrlState.js";
import { AutomationTemplateDescription } from "../../features/automations/automationTemplateDescription.js";
import { CoachingPlaybookConflictModal } from "../../features/automations/coachingPlaybookConflictModal.js";
import { useAutomationFieldsContext } from "../../features/automations/useAutomationFieldsContext.js";

/**
 * A new automation Ask Alpharun suggests, opened from its proposal card
 * (?proposalId=...) for review. Apply creates it, disabled, and opens it, as
 * the template chooser does. Without a suggestion the page sends the user to
 * the automation list.
 */
export const AutomationNewPage: FC = () => {
  const playbookId = asPlaybookId(requireRouteParam(useParams(), "playbookId"));
  const automationsHref = appPaths.playbookAutomations.build({ playbookId });
  const [searchParams] = useSearchParams();
  const proposalId = copilotProposalIdSchema.safeParse(
    searchParams.get(AUTOMATION_PROPOSAL_QUERY_PARAM),
  ).data;
  const { currentData: proposal, isError } =
    useGetPendingAutomationProposalQuery(proposalId ?? skipToken);

  if (!proposalId) {
    return <Navigate to={automationsHref} replace />;
  }

  if (isError) {
    return <UnavailableSuggestionPage />;
  }

  if (!proposal) {
    return (
      <div className="flex min-h-full items-center justify-center">
        <Loader style={LoaderStyle.ZOOMIES} />
      </div>
    );
  }

  if (proposal.data.kind !== "create_automation") {
    return <UnavailableSuggestionPage />;
  }

  return (
    <AutomationNewPageContent
      key={proposal.id}
      proposalId={proposal.id}
      mintedAutomationId={proposal.resource_id}
      playbookId={playbookId}
      templateId={proposal.data.template_id}
      proposedConfiguration={{
        name: proposal.data.name,
        settings: proposal.data.settings,
      }}
      automationsHref={automationsHref}
    />
  );
};

const UnavailableSuggestionPage: FC = () => (
  <PageLayout>
    <PageHeader title="New automation" />
    <PageBody>
      <p className="text-sm text-muted-foreground">
        This suggestion is no longer available.
      </p>
    </PageBody>
  </PageLayout>
);

interface AutomationNewPageContentProps {
  proposalId: CopilotProposalId;
  /** The id the suggestion reserved for the automation it creates. */
  mintedAutomationId: AutomationId;
  playbookId: PlaybookId;
  templateId: AutomationTemplateId;
  /** The automation as the suggestion proposes it. */
  proposedConfiguration: AutomationConfiguration;
  automationsHref: string;
}

const AutomationNewPageContent: FC<AutomationNewPageContentProps> = (props) => {
  const {
    proposalId,
    mintedAutomationId,
    playbookId,
    templateId,
    proposedConfiguration,
    automationsHref,
  } = props;
  const navigate = useNavigate();

  const {
    isReadOnly,
    playbookAndVoiceAgentOptions,
    arePlaybooksLoading,
    hasLoadedAllPlaybookOptions,
    areRecipientOptionsLoading,
    teammateOptions,
    editorAndReviewerTeammateOptions,
    groupOptions,
    managerTeammateIds,
    coachingPlaybookIdsInUse,
  } = useAutomationFieldsContext();
  const [createAutomation, { isLoading: isCreating }] =
    useCreateAutomationMutation();
  const [resolveProposal, { isLoading: isResolvingProposal }] =
    useResolveProposalMutation();
  const [fetchAutomation, { isFetching: isCheckingAutomation }] =
    useLazyAutomationByIdQuery();

  const [hasCoachingConflict, setHasCoachingConflict] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Create first, then resolve: a failed create leaves the suggestion pending,
  // so its card can be reopened. A create that already went through collides
  // on the minted id, and Apply opens that automation instead.
  const handleApplyProposal = async () => {
    setError(null);
    try {
      await createAutomation({
        template_id: templateId,
        name: proposedConfiguration.name,
        is_enabled: false,
        settings: proposedConfiguration.settings,
        session_id: uuidv4(),
        proposal_id: proposalId,
      }).unwrap();
    } catch (createError) {
      if (!isConflictQueryError(createError)) {
        setError("The automation couldn't be created. Please try again.");
        return;
      }
      // The coaching check runs before the insert, so a repeat Apply of a
      // coaching suggestion conflicts with its own automation. Any other
      // conflict is the minted id already taken.
      if (
        templateId === AutomationTemplateIds.TEAMMATE_COACHING &&
        !(await fetchAutomation(mintedAutomationId)).data
      ) {
        setHasCoachingConflict(true);
        return;
      }
    }
    // The automation exists either way, so a failed resolve only leaves the
    // card open.
    await resolveProposal({ proposalId, status: "applied" });
    navigate(
      appPaths.playbookAutomation.build({
        playbookId,
        automationId: mintedAutomationId,
      }),
      { replace: true },
    );
  };

  const handleDiscardProposal = async () => {
    setError(null);
    try {
      await resolveProposal({ proposalId, status: "discarded" }).unwrap();
    } catch (discardError) {
      // A proposal that has moved on is already not being applied.
      if (!isConflictQueryError(discardError)) {
        setError("Failed to discard the suggestion. Please try again.");
        return;
      }
    }
    navigate(automationsHref);
  };

  return (
    <PageLayout height={PageLayoutHeight.Fills}>
      <PageHeader title={proposedConfiguration.name} />

      <PageBody fills>
        <CopilotProposalBanner
          className="mx-0 mt-0 rounded-lg"
          message="Showing a new automation Ask Alpharun suggests"
          discardLabel="Discard changes"
          confirmLabel="Apply changes"
          onDiscard={() => {
            void handleDiscardProposal();
          }}
          onConfirm={() => {
            void handleApplyProposal();
          }}
          isDisabled={
            isReadOnly ||
            isCreating ||
            isCheckingAutomation ||
            isResolvingProposal
          }
        />
        <AutomationTemplateDescription templateId={templateId} />

        <AutomationConfigForm
          templateId={templateId}
          isReadOnly
          playbookAndVoiceAgentOptions={playbookAndVoiceAgentOptions}
          arePlaybooksLoading={arePlaybooksLoading}
          hasLoadedAllPlaybookOptions={hasLoadedAllPlaybookOptions}
          areRecipientOptionsLoading={areRecipientOptionsLoading}
          teammateOptions={teammateOptions}
          editorAndReviewerTeammateOptions={editorAndReviewerTeammateOptions}
          groupOptions={groupOptions}
          managerTeammateIds={managerTeammateIds}
          coachingPlaybookIdsInUse={coachingPlaybookIdsInUse}
          name={proposedConfiguration.name}
          settings={proposedConfiguration.settings}
          // The suggestion is edited once applied, on the automation's page.
          onNameChange={() => undefined}
          onSettingsChange={() => undefined}
        />

        {error ? <FieldError>{error}</FieldError> : null}
      </PageBody>

      <CoachingPlaybookConflictModal
        isOpen={hasCoachingConflict}
        onClose={() => {
          setHasCoachingConflict(false);
        }}
      />
    </PageLayout>
  );
};
